import { forwardRef, useState } from 'react'
import { ChevronRight } from 'lucide-react'
import styles from './Layout.module.css'

export function FormLayout({ children, errorSummary, actions, className = '', onSubmit }) {
  const Component = onSubmit ? 'form' : 'div'
  return <Component className={`${styles.form} ${className}`.trim()} onSubmit={onSubmit}>{errorSummary}{children}{actions ? <FormLayoutActions>{actions}</FormLayoutActions> : null}</Component>
}

export function FormLayoutSection({ title, description, children, headingLevel = 2, className = '' }) {
  const Heading = `h${headingLevel}`
  return (
    <section className={`${styles.formSection} ${className}`.trim()}>
      {title || description ? <div className={styles.formHeading}>{title ? <Heading>{title}</Heading> : null}{description ? <p>{description}</p> : null}</div> : null}
      {children}
    </section>
  )
}

export function FormLayoutRow({ columns = 1, children, className = '' }) {
  return <div className={`${styles.formRow} ${className}`.trim()} style={{ '--wf-form-columns': Math.min(3, Math.max(1, columns)) }}>{children}</div>
}

export function FormLayoutActions({ children, className = '' }) { return <div className={`${styles.formActions} ${className}`.trim()}>{children}</div> }
/**
 * A `<details>` disclosure for advanced or technical controls. Uncontrolled
 * by default (`defaultOpen`); pass `open` with `onToggle(nextOpen)` to own the
 * state, e.g. to open it when an error lands inside without remounting the
 * controls (focus and typed text stay). The ref reaches the `<details>`;
 * `className` joins its own, for a lighter look inside another control.
 */
export const AdvancedDisclosure = forwardRef(function AdvancedDisclosure({ label = 'Advanced', children, defaultOpen = false, open: openProp, onToggle, className = '' }, ref) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen)
  const controlled = openProp !== undefined
  const open = controlled ? Boolean(openProp) : uncontrolledOpen
  const handleToggle = (event) => {
    const next = event.currentTarget.open
    if (!controlled) setUncontrolledOpen(next)
    // The event also fires after a prop change; report only the operator's.
    if (next !== open) onToggle?.(next)
  }
  // The summary is a flex row, which drops the browser's disclosure marker;
  // the chevron restores the cue that it opens.
  return (
    <details ref={ref} className={`${styles.advanced} ${className}`.trim()} open={open} onToggle={handleToggle}>
      <summary><ChevronRight className={styles.advancedChevron} size={16} aria-hidden="true" />{label}</summary>
      <div className={styles.advancedContent}>{children}</div>
    </details>
  )
})
export const ErrorSummary = forwardRef(function ErrorSummary({ title = 'Check the highlighted fields', children, className = '' }, ref) {
  return (
    <div ref={ref} className={`${styles.errorSummary} ${className}`.trim()} role="alert" tabIndex={-1}>
      <strong>{title}</strong>
      <div className={styles.errorSummaryBody}>{children}</div>
    </div>
  )
})
